import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function GithubPagesPage(): Node {
  return html`
    <div>
      <h1>Deploy to GitHub Pages</h1>
      <p>GitHub Pages serves static files directly from a repository. Since there's no server-side SPA fallback, onefold apps should use <strong>hash-based routing</strong>.</p>

      <h2>Prerequisites</h2>
      <ul>
        <li>A GitHub repository with your onefold project</li>
        <li>The project builds to a <code>dist/</code> folder</li>
      </ul>

      <h2>1. Configure Hash Routing</h2>
      ${CodeBlock(`// main.ts
import { configureRouter, Router, navigate, mount } from 'onefold';

configureRouter({ hash: true });

const app = Router([...routes], () => NotFound());
mount(app, document.getElementById('app')!);

// Intercept internal links for hash-based navigation
document.addEventListener('click', (e) => {
  const anchor = (e.target as HTMLElement).closest('a');
  if (!anchor) return;
  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('http') || href.startsWith('#') || href.startsWith('mailto:')) return;
  e.preventDefault();
  navigate(href);
});

// Navigate to home on initial load
if (!location.hash || location.hash === '#/') {
  navigate('/');
}`)}

      <h2>2. Use Relative Asset Paths</h2>
      <p>Your <code>index.html</code> must use <code>./</code> relative paths so assets load correctly regardless of the repository subpath:</p>
      ${CodeBlock(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <link rel="stylesheet" href="./style.css" />
</head>
<body>
  <div id="app"></div>
  <script type="module" src="./app.js"></script>
</body>
</html>`, 'html')}

      ${Callout('Do NOT use absolute paths like /app.js — on GitHub Pages at username.github.io/repo-name/, they resolve to username.github.io/app.js which does not exist.')}

      <h2>3. GitHub Actions Workflow</h2>
      <p>Create <code>.github/workflows/deploy.yml</code>:</p>
      ${CodeBlock(`name: Deploy to GitHub Pages

on:
  push:
    branches: [main]

permissions:
  contents: read
  pages: write
  id-token: write

jobs:
  build-and-deploy:
    runs-on: ubuntu-latest
    environment:
      name: github-pages
      url: \${{ steps.deployment.outputs.page_url }}
    steps:
      - uses: actions/checkout@v4

      - uses: actions/setup-node@v4
        with:
          node-version: 22
          cache: npm

      - run: npm ci
      - run: npm run build

      - uses: actions/upload-pages-artifact@v3
        with:
          path: dist

      - id: deployment
        uses: actions/deploy-pages@v4`, 'yaml')}

      <h2>4. Enable Pages in Repository Settings</h2>
      <ol>
        <li>Go to your repo → Settings → Pages</li>
        <li>Under "Source", select <strong>GitHub Actions</strong></li>
        <li>Push to <code>main</code> — the workflow will deploy automatically</li>
      </ol>

      <h2>Manual Deploy (gh-pages branch)</h2>
      <p>Alternatively, deploy from a <code>gh-pages</code> branch:</p>
      ${CodeBlock(`# Install gh-pages
npm install -D gh-pages

# Add deploy script to package.json
# "deploy": "npm run build && gh-pages -d dist"

npm run deploy`, 'bash')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/routing/configure">configureRouter</a> — routing mode configuration details</li>
        <li><a href="/deployment/vercel">Vercel</a> — deploy with path-based routing</li>
      </ul>
    </div>
  `;
}
